import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ClearIcon from "@mui/icons-material/Clear";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import TimelineIcon from "@mui/icons-material/Timeline";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { useTranslation } from "react-i18next";
import GlassCard from "../../components/dashboard/GlassCard";
import EmptyState from "../../components/dashboard/EmptyState";
import { BreakdownChart, TimeSeriesChart, completeDailySeries } from "../../components/dashboard/AnalyticsCharts";
import ModuleLayout from "../../components/enterprise/ModuleLayout";
import { usePlatform } from "../../context/PlatformContext";
import saasApi from "../../utils/saasApi";
import { useSharedFilters, forEndpoint } from "../../hooks/useSharedFilters";
import { downloadChartCsv } from "../../utils/chartExport";

const FILTER_KEYS = ["days", "branch_id", "camera_id", "detection_type", "status", "severity"];

const formatTypeName = (type) => {
  if (!type) return "-";
  return type
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const SEVERITY_COLORS = {
  CRITICAL: "#f77783",
  HIGH: "#f3b85b",
  MEDIUM: "#60bfe7",
  LOW: "#40d6a7",
};

const ExecutiveOverview = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { showMockData } = usePlatform();
  const { filters, setFilter: updateFilter, clearFilters: clearAllFilters, pathFor } = useSharedFilters("executive");
  const [summary, setSummary] = useState(null);
  const [branches, setBranches] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    if (showMockData) {
      setLoading(false);
      setSummary({
        filters,
        period: {
          days: Number(filters.days) || 7,
          starts_on: "2026-10-14",
          ends_on: "2026-10-20",
          session_time_zone: "America/Argentina/Buenos_Aires",
        },
        metrics: {
          alerts: 142,
          previous_alerts: 118,
          critical_open: 2,
          false_alarms: 11,
          cameras_total: 16,
          cameras_online_recorded: 14,
        },
        timeline: [
          { day: "2026-10-14", count: 18 },
          { day: "2026-10-15", count: 18 },
          { day: "2026-10-16", count: 24 },
          { day: "2026-10-17", count: 19 },
          { day: "2026-10-18", count: 22 },
          { day: "2026-10-19", count: 15 },
          { day: "2026-10-20", count: 26 },
        ],
        detection_types: [
          { type: "shoplifting", count: 48 },
          { type: "intrusion", count: 32 },
          { type: "person_loitering", count: 26 },
          { type: "unattended_object", count: 18 },
          { type: "door_blocked", count: 12 },
          { type: "fall_detected", count: 6 },
        ],
        recent_alerts: [
          { id: 101, occurred_at: "2026-10-20T14:10:00Z", detection_type: "shoplifting", severity: "HIGH", status: "nueva", camera_name: "Cámara 01 - Entrada" },
          { id: 102, occurred_at: "2026-10-20T13:45:00Z", detection_type: "intrusion", severity: "CRITICAL", status: "en_revision", camera_name: "Cámara 04 - Depósito" },
          { id: 103, occurred_at: "2026-10-20T12:20:00Z", detection_type: "person_loitering", severity: "MEDIUM", status: "resuelta", camera_name: "Cámara 02 - Pasillo Central" },
          { id: 104, occurred_at: "2026-10-20T11:05:00Z", detection_type: "unattended_object", severity: "LOW", status: "falsa_alarma", camera_name: "Cámara 03 - Cajas" },
        ],
        severity_distribution: [
          { severity: "CRITICAL", count: 8 },
          { severity: "HIGH", count: 27 },
          { severity: "MEDIUM", count: 63 },
          { severity: "LOW", count: 44 },
        ],
        branches: [
          { id: 1, name: "Sucursal Norte", alert_count: 81 },
          { id: 2, name: "Sucursal Sur", alert_count: 61 },
        ],
      });
      return;
    }

    try {
      const data = await saasApi.getExecutiveSummary(forEndpoint(filters, "executive"));
      setSummary(data);
    } catch (err) {
      setSummary(null);
      setError(err.message || t("executive.unavailable"));
    } finally {
      setLoading(false);
    }
  }, [filters, showMockData, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (showMockData) {
      setBranches([]);
      setCameras([]);
      return;
    }
    Promise.all([saasApi.getBranches().catch(() => []), saasApi.getCameras().catch(() => ({ cameras: [] }))])
      .then(([branchRows, cameraPayload]) => {
        setBranches(Array.isArray(branchRows) ? branchRows : []);
        setCameras(cameraPayload.cameras || []);
      })
      .catch(() => {});
  }, [showMockData]);

  const hasActiveFilters = Boolean(
    filters.branch_id || filters.camera_id || filters.detection_type || filters.status || filters.severity
  );

  const alertLink = (extra = {}) => pathFor("/incidents", extra, summary?.period);

  const m = summary?.metrics;
  const exportChart = (name, dimension, rows) => {
    if (!summary?.period || showMockData || !rows?.length) return;
    const period = summary.period;
    const context = [period.starts_on, period.ends_on, period.session_time_zone, JSON.stringify(summary.filters || {})];
    downloadChartCsv(
      `absolluty-${name}-${period.starts_on}-${period.ends_on}`,
      ["period_start", "period_end", "timezone", "filters", dimension, t("executive.alerts")],
      rows.map(([label, count]) => [...context, label, count])
    );
  };
  const delta = m && m.previous_alerts !== null ? m.alerts - m.previous_alerts : null;
  const deltaPercent =
    m && m.previous_alerts > 0
      ? Math.round(((m.alerts - m.previous_alerts) / m.previous_alerts) * 100)
      : null;

  const camerasOnline = Number(m?.cameras_online_recorded ?? 0);
  const camerasTotal = Number(m?.cameras_total ?? 0);
  const healthScore = camerasTotal > 0 ? Math.round((camerasOnline / camerasTotal) * 100) : null;
  const falseAlarmsRate =
    m && m.alerts > 0 ? Math.round((m.false_alarms / m.alerts) * 100) : null;

  const branchOptions = showMockData ? summary?.branches || [] : branches.length ? branches : summary?.branches || [];
  const visibleCameras = cameras.filter(
    (camera) => !filters.branch_id || String(camera.branch_id) === filters.branch_id
  );


  const renderDelta = () => {
    if (delta === null || m?.previous_alerts === 0) {
      return (
        <Typography variant="caption" color="text.secondary">
          {t("executive.noComparison")}
        </Typography>
      );
    }
    const isUp = delta > 0;
    const isDown = delta < 0;
    const sign = isUp ? "+" : "";
    const color = isUp ? theme.palette.warning.main : isDown ? theme.palette.success.main : theme.palette.text.secondary;
    const Icon = isUp ? TrendingUpIcon : isDown ? TrendingDownIcon : TrendingFlatIcon;

    return (
      <Stack direction="row" alignItems="center" gap={0.5}>
        <Icon sx={{ fontSize: 16, color }} />
        <Typography variant="caption" sx={{ fontWeight: 700, color }}>
          {sign}
          {delta} {deltaPercent !== null ? `(${sign}${deltaPercent}%)` : ""}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t("executive.previousPeriod", { days: filters.days })}
        </Typography>
      </Stack>
    );
  };

  return (
    <ModuleLayout
      compactHeader
      title={t("executive.title")}
      subtitle={t("executive.subtitle")}
      dataMode={{
        mode: showMockData ? "mock" : error ? "partial" : "real",
        source: showMockData ? "Absolluty mock fixtures" : error || "Absolluty API",
      }}
      actions={
        <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" component={RouterLink} to={pathFor("/monitoring/live")}>{t("platform.nav.monitoring")}</Button>
          <ButtonGroup size="small" variant="outlined">
            {["7", "30", "90"].map((d) => (
              <Button
                key={d}
                variant={filters.days === d ? "contained" : "outlined"}
                onClick={() => updateFilter("days", d)}
              >
                {d}D
              </Button>
            ))}
          </ButtonGroup>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={loading}
          >
            {t("executive.refresh")}
          </Button>
        </Stack>
      }
    >
      {/* ─── FILTERS BAR ─── */}
      <Box component="section" sx={{ mb: 2.5, pb: 2, borderBottom: "1px solid", borderColor: "divider" }}>
        <Stack spacing={1.5}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.25,
              alignItems: "center",
              "& .MuiTextField-root": { minWidth: 140, flex: "1 1 140px", maxWidth: { md: 210 } },
            }}
          >
            <TextField
              select
              size="small"
              label={t("executive.branch")}
              value={filters.branch_id}
              onChange={(e) => updateFilter("branch_id", e.target.value)}
            >
              <MenuItem value="">{t("executive.allBranches")}</MenuItem>
              {branchOptions.map((branch) => (
                <MenuItem key={branch.id} value={String(branch.id)}>
                  {branch.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label={t("executive.camera")}
              value={filters.camera_id}
              onChange={(e) => updateFilter("camera_id", e.target.value)}
            >
              <MenuItem value="">{t("executive.allCameras")}</MenuItem>
              {visibleCameras.map((camera) => (
                <MenuItem key={camera.id} value={String(camera.id)}>
                  {camera.display_name || camera.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              label={t("executive.detection")}
              placeholder="e.g. shoplifting"
              value={filters.detection_type}
              onChange={(e) => updateFilter("detection_type", e.target.value)}
            />

            <TextField
              select
              size="small"
              label={t("executive.severity")}
              value={filters.severity}
              onChange={(e) => updateFilter("severity", e.target.value)}
            >
              <MenuItem value="">{t("executive.all")}</MenuItem>
              {["LOW", "MEDIUM", "HIGH", "CRITICAL"].map((sev) => (
                <MenuItem key={sev} value={sev}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: SEVERITY_COLORS[sev] || "text.secondary",
                      }}
                    />
                    {sev}
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label={t("executive.status")}
              value={filters.status}
              onChange={(e) => updateFilter("status", e.target.value)}
            >
              <MenuItem value="">{t("executive.all")}</MenuItem>
              {["nueva", "en_revision", "resuelta", "falsa_alarma"].map((st) => (
                <MenuItem key={st} value={st}>
                  {st.replaceAll("_", " ")}
                </MenuItem>
              ))}
            </TextField>

            {hasActiveFilters && (
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                startIcon={<ClearIcon />}
                onClick={clearAllFilters}
                sx={{ height: 40 }}
              >
                {t("executive.clearFilters")}
              </Button>
            )}
          </Box>

          {/* Date range & metadata caption */}
          {summary?.period && (
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
              <Typography variant="caption" color="text.secondary">
                {t("executive.source", {
                  from: summary.period.starts_on,
                  to: summary.period.ends_on,
                  timezone: summary.period.session_time_zone || "UTC",
                })}
              </Typography>
              {hasActiveFilters && (
                <Chip
                  size="small"
                  label={t("executive.activeFiltersCount", {
                    count: FILTER_KEYS.filter((key) => key !== "days" && filters[key]).length,
                  })}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Stack>
          )}
        </Stack>
      </Box>

      {/* ─── ERROR ALERT ─── */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={load}>
              {t("executive.refresh")}
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* ─── OPERATIONAL HEALTH BANNER ─── */}
      {!loading && m && (
        <Box sx={{ mb: 2.5 }}>
          {m.critical_open > 0 ? (
            <Alert
              severity="error"
              action={
                <Button
                  component={RouterLink}
                  to={alertLink({ critical_open: "1" })}
                  color="inherit"
                  size="small"
                  endIcon={<ArrowForwardIcon />}
                >
                  {t("executive.actionRequired")}
                </Button>
              }
            >
              {t("executive.attentionNotice", { count: m.critical_open })}
            </Alert>
          ) : null}
        </Box>
      )}

      {/* ─── 4 KPI METRICS GRID ─── */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {/* KPI 1: Total Alerts */}
        <Grid item xs={12} sm={6} lg={3}>
          <GlassCard
            component={RouterLink}
            to={alertLink()}
            hover
            sx={{
              display: "block",
              height: "100%",
              p: 2.5,
              textDecoration: "none",
              color: "text.primary",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {t("executive.alerts")}
              </Typography>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "12px",
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: "primary.main",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <TimelineIcon fontSize="small" />
              </Box>
            </Stack>

            {loading ? (
              <Skeleton width={100} height={44} sx={{ my: 1 }} />
            ) : (
              <Typography
                sx={{
                  mt: 1.5,
                  mb: 0.5,
                  fontSize: 32,
                  fontWeight: 700,
                  fontFamily: theme.custom.monoFont,
                  fontVariantNumeric: "tabular-nums",
                  lineHeight: 1.2,
                }}
              >
                {m?.alerts ?? "-"}
              </Typography>
            )}

            <Box sx={{ mt: 1 }}>{loading ? <Skeleton width="60%" height={16} /> : renderDelta()}</Box>
          </GlassCard>
        </Grid>

        {/* KPI 2: Critical Open */}
        <Grid item xs={12} sm={6} lg={3}>
          <GlassCard
            component={RouterLink}
            to={alertLink({ critical_open: "1" })}
            hover
            sx={{
              display: "block",
              height: "100%",
              p: 2.5,
              textDecoration: "none",
              color: "text.primary",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {t("executive.criticalOpen")}
              </Typography>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "12px",
                  bgcolor: alpha(theme.palette.error.main, 0.12),
                  color: "error.main",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <ErrorOutlineIcon fontSize="small" />
              </Box>
            </Stack>

            {loading ? (
              <Skeleton width={80} height={44} sx={{ my: 1 }} />
            ) : (
              <Typography
                sx={{
                  mt: 1.5,
                  mb: 0.5,
                  fontSize: 32,
                  fontWeight: 700,
                  fontFamily: theme.custom.monoFont,
                  fontVariantNumeric: "tabular-nums",
                  lineHeight: 1.2,
                  color: m?.critical_open > 0 ? "error.main" : "text.primary",
                }}
              >
                {m?.critical_open ?? "-"}
              </Typography>
            )}

            <Box sx={{ mt: 1 }}>
              {loading ? (
                <Skeleton width="50%" height={16} />
              ) : m?.critical_open > 0 ? (
                <Chip
                  size="small"
                  label={t("executive.actionRequired")}
                  color="error"
                  variant="outlined"
                  sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
                />
              ) : (
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "success.main" }} />
                  <Typography variant="caption" color="success.main" fontWeight={600}>
                    {t("executive.allClear")}
                  </Typography>
                </Stack>
              )}
            </Box>
          </GlassCard>
        </Grid>

        {/* KPI 3: False Alarms */}
        <Grid item xs={12} sm={6} lg={3}>
          <GlassCard
            component={RouterLink}
            to={alertLink({ status: "falsa_alarma" })}
            hover
            sx={{
              display: "block",
              height: "100%",
              p: 2.5,
              textDecoration: "none",
              color: "text.primary",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {t("executive.falseAlarms")}
              </Typography>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "12px",
                  bgcolor: alpha(theme.palette.warning.main, 0.12),
                  color: "warning.main",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <WarningAmberOutlinedIcon fontSize="small" />
              </Box>
            </Stack>

            {loading ? (
              <Skeleton width={80} height={44} sx={{ my: 1 }} />
            ) : (
              <Typography
                sx={{
                  mt: 1.5,
                  mb: 0.5,
                  fontSize: 32,
                  fontWeight: 700,
                  fontFamily: theme.custom.monoFont,
                  fontVariantNumeric: "tabular-nums",
                  lineHeight: 1.2,
                }}
              >
                {m?.false_alarms ?? "-"}
              </Typography>
            )}

            <Box sx={{ mt: 1 }}>
              {loading ? (
                <Skeleton width="60%" height={16} />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {falseAlarmsRate === null ? t("executive.noComparison") : t("executive.falseAlarmsRate", { rate: falseAlarmsRate })}
                </Typography>
              )}
            </Box>
          </GlassCard>
        </Grid>

        {/* KPI 4: Cameras Health */}
        <Grid item xs={12} sm={6} lg={3}>
          <GlassCard
            component={RouterLink}
            to="/monitoring/cameras"
            hover
            sx={{
              display: "block",
              height: "100%",
              p: 2.5,
              textDecoration: "none",
              color: "text.primary",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {t("executive.cameras")}
              </Typography>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "12px",
                  bgcolor: alpha(theme.palette.success.main, 0.12),
                  color: "success.main",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <CameraAltOutlinedIcon fontSize="small" />
              </Box>
            </Stack>

            {loading ? (
              <Skeleton width={90} height={44} sx={{ my: 1 }} />
            ) : (
              <Typography
                sx={{
                  mt: 1.5,
                  mb: 0.5,
                  fontSize: 32,
                  fontWeight: 700,
                  fontFamily: theme.custom.monoFont,
                  fontVariantNumeric: "tabular-nums",
                  lineHeight: 1.2,
                }}
              >
                {m ? `${camerasOnline} / ${camerasTotal}` : "-"}
              </Typography>
            )}

            <Box sx={{ mt: 1 }}>
              {loading ? (
                <Skeleton width="70%" height={16} />
              ) : healthScore !== null ? (
                <>
                  <LinearProgress
                    variant="determinate"
                    value={healthScore}
                    sx={{
                      height: 5,
                      borderRadius: 3,
                      bgcolor: "action.hover",
                      "& .MuiLinearProgress-bar": {
                        bgcolor:
                          healthScore >= 90
                            ? "success.main"
                            : healthScore >= 75
                            ? "warning.main"
                            : "error.main",
                      },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    {t("executive.recordedStatus")}
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {t("executive.recordedStatus")}
                </Typography>
              )}
            </Box>
          </GlassCard>
        </Grid>
      </Grid>

      {/* ─── CHARTS SECTION: TIMELINE (8) + BY DETECTION (4) ─── */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {/* Timeline Bar Chart */}
        <Grid item xs={12} lg={8}>
          <Box component="section" sx={{ height: "100%", py: 2, borderTop: "1px solid", borderColor: "divider" }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={1} mb={2}>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  {t("executive.trend")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("executive.trendDetail")}
                </Typography>
              </Box>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <IconButton aria-label={`${t("executive.exportCsv")}: ${t("executive.trend")}`} title={t("executive.exportCsv")} disabled={showMockData || loading || !!error || !summary?.timeline?.length} onClick={() => exportChart("trend", t("executive.date"), (completeDailySeries(summary.timeline, summary.period) || []).map((row) => [row.day, row.count]))}><FileDownloadOutlinedIcon /></IconButton>
                <Button component={RouterLink} to={alertLink()} size="small" endIcon={<ArrowForwardIcon />}>{t("executive.viewAll")}</Button>
              </Stack>
            </Stack>

            <Box sx={{ minHeight: 290, width: "100%", minWidth: 0 }}>
              {loading ? (
                <Skeleton variant="rounded" height={280} />
              ) : !summary?.timeline?.length ? (
                <EmptyState
                  title={t("executive.noData")}
                  description={t("executive.noDataHint")}
                  compact
                />
              ) : (
                <TimeSeriesChart
                  rows={summary.timeline}
                  period={summary.period}
                  unit={t("executive.alerts").toLowerCase()}
                  locale={i18n.language}
                  selectLabel={t("executive.clickToFilterDay")}
                  emptyLabel={t("executive.noData")}
                  onSelectDay={(day) => navigate(alertLink({ date_from: day, date_to: day }))}
                />
              )}
            </Box>
          </Box>
        </Grid>

        {/* Breakdown by Detection Type */}
        <Grid item xs={12} lg={4}>
          <Box component="section" sx={{ height: "100%", py: 2, borderTop: "1px solid", borderColor: "divider" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1} mb={2}>
              <Box><Typography variant="h6" fontWeight={700}>{t("executive.byDetection")}</Typography><Typography variant="caption" color="text.secondary">{t("executive.selectDetection")}</Typography></Box>
              <IconButton aria-label={`${t("executive.exportCsv")}: ${t("executive.byDetection")}`} title={t("executive.exportCsv")} disabled={showMockData || loading || !!error || !summary?.detection_types?.length} onClick={() => exportChart("detections", t("executive.byDetection"), summary.detection_types.map((row) => [row.type, row.count]))}><FileDownloadOutlinedIcon /></IconButton>
            </Stack>

            {loading ? (
              <Stack spacing={1.5}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} height={40} />
                ))}
              </Stack>
            ) : !summary?.detection_types?.length ? (
              <EmptyState title={t("executive.noData")} compact />
            ) : (
              <BreakdownChart rows={summary.detection_types} total={m?.alerts} getKey={(row) => row.type} getLabel={(row) => formatTypeName(row.type)} getCount={(row) => row.count} getHref={(row) => alertLink({ detection_type: row.type })} locale={i18n.language} unit={t("executive.alerts").toLowerCase()} emptyLabel={t("executive.noData")} />
            )}
          </Box>
        </Grid>
      </Grid>

      {/* ─── SEVERITY DISTRIBUTION & BRANCH BREAKDOWN (6 / 6) ─── */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {/* Severity Distribution */}
        <Grid item xs={12} md={6}>
          <Box component="section" sx={{ height: "100%", py: 2, borderTop: "1px solid", borderColor: "divider" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1} mb={2}>
              <Box><Typography variant="h6" fontWeight={700}>{t("executive.severityDistribution")}</Typography><Typography variant="caption" color="text.secondary">{t("executive.severitySource")}</Typography></Box>
              <IconButton aria-label={`${t("executive.exportCsv")}: ${t("executive.severityDistribution")}`} title={t("executive.exportCsv")} disabled={showMockData || loading || !!error || !summary?.severity_distribution?.length} onClick={() => exportChart("severity", t("executive.severity"), summary.severity_distribution.map((row) => [row.severity || "UNKNOWN", row.count]))}><FileDownloadOutlinedIcon /></IconButton>
            </Stack>

            {loading ? (
              <Skeleton variant="rounded" height={80} />
            ) : (
              <BreakdownChart rows={summary?.severity_distribution} total={m?.alerts} getKey={(row) => row.severity} getLabel={(row) => row.severity || "UNKNOWN"} getCount={(row) => row.count} getHref={(row) => alertLink({ severity: row.severity || "UNKNOWN" })} colorFor={(row) => SEVERITY_COLORS[row.severity] || theme.palette.text.secondary} locale={i18n.language} unit={t("executive.alerts").toLowerCase()} emptyLabel={t("executive.noData")} />
            )}
          </Box>
        </Grid>

        {/* Branches Overview */}
        <Grid item xs={12} md={6}>
          <Box component="section" sx={{ height: "100%", py: 2, borderTop: "1px solid", borderColor: "divider" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1} mb={2}>
              <Box><Typography variant="h6" fontWeight={700}>{t("executive.branchOverview")}</Typography><Typography variant="caption" color="text.secondary">{t("executive.branchDetail")}</Typography></Box>
              <IconButton aria-label={`${t("executive.exportCsv")}: ${t("executive.branchOverview")}`} title={t("executive.exportCsv")} disabled={showMockData || loading || !!error || !summary?.branches?.length} onClick={() => exportChart("branches", t("executive.branchOverview"), summary.branches.map((row) => [row.name, row.alert_count]))}><FileDownloadOutlinedIcon /></IconButton>
            </Stack>

            {loading ? (
              <Stack spacing={1.5}>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} height={36} />
                ))}
              </Stack>
            ) : !summary?.branches?.length ? (
              <EmptyState title={t("executive.noData")} compact />
            ) : (
              <BreakdownChart rows={summary.branches} total={m?.alerts} getKey={(row) => row.id} getLabel={(row) => row.name} getCount={(row) => row.alert_count} getHref={(row) => alertLink({ branch_id: String(row.id) })} locale={i18n.language} unit={t("executive.alerts").toLowerCase()} emptyLabel={t("executive.noData")} />
            )}
          </Box>
        </Grid>
      </Grid>

      {/* ─── RECENT ALERTS TABLE ─── */}
      <Box component="section" sx={{ py: 2, borderTop: "1px solid", borderColor: "divider" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {t("executive.recent")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("executive.recentDetail")}
            </Typography>
          </Box>
          <Button
            component={RouterLink}
            to={alertLink()}
            endIcon={<ArrowForwardIcon />}
            size="small"
            variant="outlined"
          >
            {t("executive.viewAll")}
          </Button>
        </Stack>

        {loading ? (
          <Stack spacing={1}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} height={42} />
            ))}
          </Stack>
        ) : !summary?.recent_alerts?.length ? (
          <EmptyState title={t("executive.noData")} compact />
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small" aria-label={t("executive.recent")}>
              <TableHead>
                <TableRow>
                  <TableCell>{t("executive.date")}</TableCell>
                  <TableCell>{t("executive.camera")}</TableCell>
                  <TableCell>{t("executive.detection")}</TableCell>
                  <TableCell>{t("executive.severity")}</TableCell>
                  <TableCell>{t("executive.status")}</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.recent_alerts.map((alert) => (
                  <TableRow key={alert.id} hover sx={{ "& .MuiTableCell-root": { color: "text.primary" } }}>
                    <TableCell sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                      {alert.occurred_at
                        ? new Date(alert.occurred_at).toLocaleString(i18n.language)
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" gap={0.75}>
                        <CameraAltOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                        <Typography variant="body2" fontWeight={500}>
                          {alert.camera_name || "-"}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={formatTypeName(alert.detection_type)}
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={alert.severity || "-"}
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(
                            SEVERITY_COLORS[alert.severity] || theme.palette.text.secondary,
                            0.14
                          ),
                          color: SEVERITY_COLORS[alert.severity] || "text.primary",
                          border: `1px solid ${
                            SEVERITY_COLORS[alert.severity] || theme.palette.divider
                          }`,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {(alert.status || "").replaceAll("_", " ")}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" component={RouterLink} to={`/incidents?alert_id=${alert.id}`} aria-label={`${t("executive.viewAll")} #${alert.id}`}>
                        <ArrowForwardIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </ModuleLayout>
  );
};

export default ExecutiveOverview;
